import { useEffect, useState } from 'react';
import {
    Autocomplete,
    Button,
    CircularProgress,
    Stack,
    TextField,
    Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { useNotify } from 'ra-core';

import { ContractorOwnerFormDialog } from '@/components/ContractorOwnerFormDialog';
import { ContractorOwner } from '@/types';
import {
    contractorOwnerOptionLabel,
    createContractorOwner,
    searchContractorOwners,
    updateContractorOwner,
    updateProjectContacts,
} from '@/utils/contractor_owner_utils';

type ContractorOwnerAssignControlProps = {
    value: ContractorOwner | null;
    onChange: (person: ContractorOwner | null) => void;
    /** When set, selecting or creating immediately assigns as project contractor. */
    projectId?: number | null;
    disabled?: boolean;
    size?: 'small' | 'medium';
    error?: boolean;
    helperText?: string;
    label?: string;
};

export const ContractorOwnerAssignControl = ({
    value,
    onChange,
    projectId = null,
    disabled = false,
    size = 'small',
    error = false,
    helperText,
    label = 'Contractor',
}: ContractorOwnerAssignControlProps) => {
    const notify = useNotify();
    const [options, setOptions] = useState<ContractorOwner[]>([]);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [assigning, setAssigning] = useState(false);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit' | null>(null);

    const canEdit = value != null && Number(value.id) > 0;
    const labelLower = label.toLowerCase();

    useEffect(() => {
        setInput(value ? contractorOwnerOptionLabel(value) : '');
    }, [value]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        searchContractorOwners(input)
            .then(opts => {
                if (!cancelled) setOptions(opts);
            })
            .catch(() => {
                if (!cancelled) setOptions([]);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [input]);

    const closeDialog = () => {
        setDialogMode(null);
    };

    const applyPerson = async (person: ContractorOwner | null): Promise<boolean> => {
        if (person == null) {
            onChange(null);
            return true;
        }
        if (projectId == null) {
            onChange(person);
            return true;
        }
        if (Number(value?.id) === Number(person.id)) {
            onChange(person);
            return true;
        }
        setAssigning(true);
        try {
            await updateProjectContacts(Number(projectId), {
                contractor_id: Number(person.id),
            });
            onChange(person);
            notify('Contractor assigned to project', { type: 'success' });
            return true;
        } catch (e) {
            notify(e instanceof Error ? e.message : 'Failed to assign contractor', {
                type: 'error',
            });
            return false;
        } finally {
            setAssigning(false);
        }
    };

    const busy = disabled || assigning;

    return (
        <>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'flex-start' }}
            >
                <Autocomplete
                    sx={{ flex: 1, minWidth: 220 }}
                    options={options}
                    loading={loading}
                    value={value}
                    inputValue={input}
                    onInputChange={(_, v) => setInput(v)}
                    onChange={(_, v) => void applyPerson(v)}
                    getOptionLabel={contractorOwnerOptionLabel}
                    isOptionEqualToValue={(a, b) => Number(a.id) === Number(b.id)}
                    disabled={busy}
                    renderInput={params => (
                        <TextField
                            {...params}
                            label={label}
                            size={size}
                            error={error}
                            helperText={helperText}
                            FormHelperTextProps={{
                                sx: error ? { color: 'error.main', fontSize: 11 } : undefined,
                            }}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {loading || assigning ? (
                                            <CircularProgress color="inherit" size={16} />
                                        ) : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
                <div>
                    <Tooltip title={`Create ${labelLower}`}>
                        <span>
                            <Button
                                variant="contained"
                                size={size}
                                startIcon={<PersonAddIcon />}
                                onClick={() => setDialogMode('create')}
                                disabled={busy}
                                sx={{ textTransform: 'none', flexShrink: 0, marginBottom: 0.5 }}
                            />
                        </span>
                    </Tooltip>
                    <br />
                    <Tooltip title={`Edit ${labelLower}`}>
                        <span>
                            <Button
                                variant="outlined"
                                size={size}
                                startIcon={<EditIcon />}
                                onClick={() => setDialogMode('edit')}
                                disabled={busy || !canEdit}
                                sx={{ textTransform: 'none', flexShrink: 0 }}
                            />
                        </span>
                    </Tooltip>
                </div>
            </Stack>

            <ContractorOwnerFormDialog
                open={dialogMode != null}
                mode={dialogMode ?? 'create'}
                contractor={dialogMode === 'edit' ? value : null}
                label={labelLower}
                onClose={closeDialog}
                onSave={async payload => {
                    if (dialogMode === 'create') {
                        const created = await createContractorOwner(payload);
                        const assigned = await applyPerson(created);
                        if (!assigned) {
                            throw new Error(
                                'Contractor was created but could not be assigned to the project',
                            );
                        }
                        if (projectId == null) {
                            notify('Contractor created', { type: 'success' });
                        }
                        return created;
                    }
                    if (value == null) {
                        throw new Error('No contractor selected');
                    }
                    const updated = await updateContractorOwner(Number(value.id), payload);
                    onChange(updated);
                    notify('Contractor updated', { type: 'success' });
                    return updated;
                }}
            />
        </>
    );
};
